//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Radio from '@material-ui/core/Radio';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getFormattedDate } from '../../common/dateHelper';
import { getUserDateFormat } from '../../common/dateHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { changeProfileDraft } from '../../common/services/myProfileServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
// import AfitdLink from '../../common/elemental/AfitdLink';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import ResumeDialog from './ResumeDialog';
import PlaceholderText from './PlaceholderText';
import FieldLabel from './FieldLabel';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
import _get from 'lodash/get';
// import _set from 'lodash/set';
import _isNil from 'lodash/isNil';
import { CONFIRM_DIALOG_MESSAGE_CHANGE_JS_PROFILE_TO_DRAFT } from '../../common/constants';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        root: {
            backgroundColor: '#FFFFFF',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
        },
        // .aboutCnt.mght h4
        titleContainer: { padding: '0 0 19px 0' },
        // .aboutMain
        editButtonContainer: {
            // textAlign: 'right',
        },
        // .aboutMain
        editButton: { color: '#FC821F', padding: '0 5px 19px 0' },
        // .develop
        resumeContainer: { padding: '0 0 80px 0' },
        // .dFlex {  display: flex; display: -webkit-flex; display: -ms-flexbox;}
        resumeList1: {
            display: 'flex',
            justifyContent: 'space-between',
            gap: '16px',
            [theme.breakpoints.down('sm')]: { flexDirection: 'column' },
        },
        resumeList2: {
            marginTop: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '16px',
            [theme.breakpoints.down('sm')]: { flexDirection: 'column' },
        },
        // .develop ul li.file{width: 100%;padding: 0px 0px 42px;}
        resumeListItem: {
            flex: 1,
            // padding: '0px 0px 42px',
        },
        // // .fileTab{display:inline-block;padding: 1px 15px 69px;border: 1px dashed #D5D7DD;width: 100%;border-radius: 5px;}
        // resumeBlock: {
        //     height: '130px',
        //     // margin: '0 8px',
        //     padding: '20px',
        //     border: '1px dashed #D5D7DD',
        //     borderRadius: '5px',
        // },
        // resumeBlockPopulated: {
        resumeBlock: {
            // margin: '0 8px',
            height: '100px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            // justifyContent: 'space-between',
            padding: '18px',
            border: '2px solid #1D8A8B',
            borderRadius: '4px',
        },
        resumeBlockPlaceholder: {
            border: '1px dashed #D5D7DD',
        },
        fileLink: {
            textDecoration: 'none',
        },
        resumeHeaderContainer: {
            flex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            height: '25px',
            marginBottom: '22px',
        },
        resumeHeaderLeft: {
            maxHeight: '50px',
            display: 'flex',
            margin: 0,
        },
        resumeHeaderRight: {
            textAlign: 'right',
            color: '#FFFFFF',
            fontWeight: 500,
            marginLeft: '24px',
        },
        resumeRadioLabel: {
            alignItems: 'flex-start',
        },
        resumeRadio: {
            paddingTop: 0,
        },
        activeResume: {
            // alignItems: 'flex-start',
            background: '#1D8A8B',
            color: '#FFFFFF',
            fontWeight: 500,
            '&$checked': {
                color: '#FFFFFF',
                paddingTop: 0,
            },
        },
        activeResumeRadio: {
            // alignItems: 'flex-start',
            // background: '#1D8A8B',
            // color: '#FFFFFF',
            // fontWeight: 500,
            '&$checked': {
                color: '#FFFFFF',
            },
        },
        updateDateContainer: {
            display: 'flex',
            alignItems: 'flex-end',
            [theme.breakpoints.up('xl')]: { height: '50px' },
        },
        lastUpdatedLabelText: {
            marginRight: '4px',
            fontStyle: 'italic',
        },
        // updateDateText: {
        //     fontStyle: 'italic',
        // },
        checked: {},
        invisible: {
            visibility: 'hidden',
        },
        hidden: {
            display: 'none',
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, props, editDialogOpen, confirmEditProfileDialogOpen, setConfirmEditProfileDialogOpen, handleConfirmEditProfile } = params;
    const { setEditDialogOpen } = params;
    const { data } = props;
    const { resumeList2: resumeList2Cls, hidden: hiddenCls } = classes;

    const resumeList = _get(data, 'resumeList', []);
    const resume3Data = _get(resumeList, '[2]');
    const resume4Data = _get(resumeList, '[3]');
    // const editLinkLabel = resumeList.length === 0 ? 'Add' : 'Edit';
    // const isProfilePublic = _get(data, 'isPublic');

    const defns = {
        titleText: {
            variant: 'h6',
            clsLightBold: true,
            value: 'Resume',
        },
        // editLink: {
        //     label: editLinkLabel,
        //     onClick: () => {
        //         if (isProfilePublic) setConfirmEditProfileDialogOpen(true);
        //         else setEditDialogOpen(true);
        //     },
        // },
        editDialog: {
            open: editDialogOpen,
            data,
            onClose: handleCloseEditDialog.bind(null, setEditDialogOpen),
        },
        resume1: {
            idx: 0,
            data: _get(resumeList, '[0]'),
            isFirst: true,
        },
        resume2: {
            idx: 1,
            data: _get(resumeList, '[1]'),
        },
        resumeList2: {
            className: cx(resumeList2Cls, { [hiddenCls]: _isNil(resume3Data) }),
        },
        resume3: {
            idx: 2,
            data: resume3Data,
            // isHidden: true, //_isNil(resume3Data), // if resume #3 is null, do not even display placeholder
        },
        resume4: {
            idx: 3,
            data: resume4Data,
            // isHidden: true, //_isNil(resume3Data), // if resume #3 is null, do not even display placeholder
        },
        confirmEditProfileDialog: {
            maxWidth: 'sm',
            title: 'Edit Your Profile?',
            onClose: () => setConfirmEditProfileDialogOpen(false),
            onConfirm: handleConfirmEditProfile,
            open: confirmEditProfileDialogOpen,
            message: CONFIRM_DIALOG_MESSAGE_CHANGE_JS_PROFILE_TO_DRAFT,
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
// const handleClickEditLink = (setEditDialogOpen, evt) => {
//     setEditDialogOpen(true);
// };

const handleCloseEditDialog = (setEditDialogOpen, evt) => {
    setEditDialogOpen(false);
};

const ResumePopulated = props => {
    const classes = useStyles(/*{param1, param2}*/)();
    const appContext = useContext(AppContext);
    const userDateFormat = getUserDateFormat(appContext);
    const { data /*, idx*/ } = props;
    const {
        resumeBlock: resumeBlockCls,
        resumeRadioLabel: resumeRadioLabelCls,
        resumeRadio: resumeRadioCls,
        activeResume: activeResumeCls,
        activeResumeRadio: activeResumeRadioCls,
        checked: checkedCls,
        // resumeHeaderRight: resumeHeaderRightCls,
        // invisible: invisibleCls,
    } = classes;

    const isActive = _get(data, 'isActive', false);
    const title = _get(data, 'title', '');
    const url = _get(data, 'attachment.url');
    const updateDate = getFormattedDate(_get(data, 'updateDt'), userDateFormat);

    const fileLinkHref = _isNil(url) ? undefined : url;

    const defns = {
        resumeBlock: {
            className: cx(resumeBlockCls, { [activeResumeCls]: isActive }),
        },
        fileLink: {
            target: '_blank',
            href: fileLinkHref,
        },
        titleText: {
            variant: 'body1',
            clsSkinnyBold: true,
            clsWhiteColor: isActive,
            value: title,
        },
        // resumeHeaderRight: {
        //     className: cx(resumeHeaderRightCls, { [invisibleCls]: !isActive }),
        // },
        activeText: {
            variant: 'body1',
            clsSkinnyBold: true,
            clsWhiteColor: isActive,
            clsGrayColor: !isActive,
            value: isActive ? 'Active' : 'Inactive',
        },
        lastUpdatedLabelText: {
            variant: 'caption',
            clsWhiteColor: isActive,
            clsSkinnyBold: true,
        },
        updateDateText: {
            variant: 'caption',
            clsWhiteColor: isActive,
            value: updateDate,
        },
        isActiveFormControlLabel: {
            className: cx(resumeRadioLabelCls, { [activeResumeCls]: isActive }),
            value: 'active',
            control: <Radio classes={{ root: cx(resumeRadioCls, { [activeResumeRadioCls]: isActive }), checked: checkedCls }} />,
            label: (
                <AfitdTypography variant="body1" clsSkinnyBold={true} clsWhiteColor={isActive}>
                    Active
                </AfitdTypography>
            ),
            labelPlacement: 'start',
            checked: isActive, //isActiveChecked,
            // onClick: handleClickIsActive.bind(null, dispatch, idx, setIsDirty),
        },
    };
    setClassNameInDefns(defns, classes);

    return (
        <div {...defns.resumeBlock}>
            <a {...defns.fileLink}>
                <div {...defns.resumeHeaderContainer}>
                    <div {...defns.resumeHeaderLeft}>
                        <AfitdTypography {...defns.titleText} />
                    </div>
                    <div {...defns.resumeHeaderRight}>
                        <AfitdTypography {...defns.activeText} />
                        {/* <RadioGroup aria-label="isActive" name="isActive" value={'active'}>
                        <FormControlLabel {...defns.isActiveFormControlLabel} />
                    </RadioGroup> */}
                    </div>
                </div>
                <div {...defns.updateDateContainer}>
                    <AfitdTypography {...defns.lastUpdatedLabelText}>Last Updated:</AfitdTypography>
                    <AfitdTypography {...defns.updateDateText} />
                </div>
            </a>
        </div>
    );
};

const ResumePlaceholder = props => {
    const classes = useStyles(/*{param1, param2}*/)();
    const { idx } = props;
    const {
        resumeBlock: resumeBlockCls,
        resumeBlockPlaceholder: resumeBlockPlaceholderCls,
        resumeRadioLabel: resumeRadioLabelCls,
        resumeRadio: resumeRadioCls,
        checked: checkedCls,
        resumeHeaderRight: resumeHeaderRightCls,
        invisible: invisibleCls,
    } = classes;

    // Keep the radio button here but stay invisible so that the placeholder width is same as that of the populated blocks
    const defns = {
        resumeBlock: {
            className: cx(resumeBlockCls, resumeBlockPlaceholderCls),
        },
        resumeHeaderRight: {
            className: cx(resumeHeaderRightCls, invisibleCls),
        },
        activeText: {
            variant: 'body1',
            clsSkinnyBold: true,
            value: 'Active',
        },
        isActiveFormControlLabel: {
            className: cx(resumeRadioLabelCls, invisibleCls),
            value: 'active',
            control: <Radio classes={{ root: resumeRadioCls, checked: checkedCls }} />,
            label: (
                <AfitdTypography variant="body1" clsSkinnyBold={true}>
                    Active
                </AfitdTypography>
            ),
            labelPlacement: 'start',
        },
    };
    setClassNameInDefns(defns, classes);

    return (
        // <div {...defns.resumeListItem}>
        //     <div {...defns.resumeBlock}>
        //         <PlaceholderText value={`+ Add resume #${idx + 1}`} />
        //     </div>
        // </div>
        <div {...defns.resumeBlock}>
            <div {...defns.resumeHeaderContainer}>
                <div {...defns.resumeHeaderLeft}>
                    <PlaceholderText value={`+ Add resume #${idx + 1}`} />
                </div>
                <div {...defns.resumeHeaderRight}>
                    <AfitdTypography {...defns.activeText} />
                    {/* <RadioGroup aria-label="isActive" name="isActive" value={'active'}>
                        <FormControlLabel {...defns.isActiveFormControlLabel} />
                    </RadioGroup> */}
                </div>
            </div>
        </div>
    );
};

const ResumeBlock = props => {
    // const classes = useStyles(/*{param1, param2}*/)();
    const { data, idx, isHidden } = props;

    if (_isNil(data)) {
        return isHidden ? null : <ResumePlaceholder {...{ idx }} />;
    } else {
        return <ResumePopulated {...props} />;
    }
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const Resume = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    // const appContext = useContext(AppContext);
    const { userId } = useContext(AppContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = useContext(LayoutContext);

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    // const [editDialogOpen, setEditDialogOpen] = useState();
    const [confirmEditProfileDialogOpen, setConfirmEditProfileDialogOpen] = useState(false);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { editDialogOpen, setEditDialogOpen } = props;

    //---------------------------------------------------------------------
    // Handlers
    //---------------------------------------------------------------------
    const handleConfirmEditProfile = async () => {
        const success = await changeProfileDraft({ userId, setSnackbarMessage, setSnackbarErrorMessage });
        if (success) setEditDialogOpen(true);
    };

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // useEffect(() => {
    //     const newDefns = getDefns(props, { });
    //     setDefns(newDefns);
    // }, [props]);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, editDialogOpen, setEditDialogOpen, confirmEditProfileDialogOpen, setConfirmEditProfileDialogOpen, handleConfirmEditProfile });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <div {...defns.header}>
                <div {...defns.titleContainer}>
                    <AfitdTypography {...defns.titleText} />
                </div>
                {/* <div {...defns.editButtonContainer}>
                    <AfitdLink {...defns.editLink} />
                </div> */}
            </div>
            <div {...defns.resumeContainer}>
                <FieldLabel>Resume:</FieldLabel>
                <div {...defns.resumeList1}>
                    <ResumeBlock {...defns.resume1} />
                    <ResumeBlock {...defns.resume2} />
                </div>
                <div {...defns.resumeList2}>
                    <ResumeBlock {...defns.resume3} />
                    <ResumeBlock {...defns.resume4} />
                </div>
            </div>
            <AfitdConfirmDialog {...defns.confirmEditProfileDialog} />
            {editDialogOpen && <ResumeDialog {...defns.editDialog} />}
        </div>
    );
};

export default Resume;
